import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElement,
} from '@anchorage/graphql/generated';
import { KYCQuestionnaireElementType } from '@anchorage/graphql/generated';

import type { FormAnswer } from './types';
import { getElementQuestions, questionHasAnswer } from './utils';

/**
 * One mobile view: a section of a page, rendered on its own screen.
 *
 * `sectionId` is the id of the view's first element: the level-1 BLOCK id in
 * onboarding data, the first element's id for a run of non-BLOCK elements and
 * the page id for a page without any view. It is never an index, so it
 * survives sections being shown or hidden by triggers.
 */
export type SectionView = {
  /** Leaf NAV id: the `page` URL param. */
  pageId: string;
  /** Stable element id: the `section` URL param. */
  sectionId: string;
  /** Block title, or the page title when the block has none. */
  title: string;
  /** True on the first view of a page, which renders the page title above the section. */
  isFirstInPage: boolean;
  /** The level-0 BLOCK the view came from, if any. */
  pageHeader?: KYCQuestionnaireElement;
  /** What to render. */
  elements: KYCQuestionnaireElement[];
};

export type SectionStatus = 'default' | 'completed' | 'error';

type NonEmptyElements = [KYCQuestionnaireElement, ...KYCQuestionnaireElement[]];

type SiblingGroup =
  | { block: KYCQuestionnaireElement; run?: undefined }
  | { block?: undefined; run: NonEmptyElements };

const isBlock = (element: KYCQuestionnaireElement) =>
  element.type === KYCQuestionnaireElementType.BLOCK;

// Keeps every BLOCK on its own and joins consecutive non-BLOCK siblings into
// one run, in document order.
const groupSiblings = (
  elements: KYCQuestionnaireElement[] = [],
): SiblingGroup[] => {
  const groups: SiblingGroup[] = [];
  let run: NonEmptyElements | undefined;

  for (const element of elements) {
    if (isBlock(element)) {
      groups.push({ block: element });
      run = undefined;
    } else if (run) {
      run.push(element);
    } else {
      run = [element];
      groups.push({ run });
    }
  }

  return groups;
};

const getPageViews = (
  page: KYCQuestionnaireElement,
  shouldShow: (element: KYCQuestionnaireElement) => boolean,
): SectionView[] => {
  const views: SectionView[] = [];
  let pageHeader: KYCQuestionnaireElement | undefined;

  const addView = (
    view: Pick<SectionView, 'sectionId' | 'title' | 'pageHeader' | 'elements'>,
  ) => {
    views.push({ pageId: page.id, isFirstInPage: views.length === 0, ...view });
  };
  const addRun = (run: NonEmptyElements, header?: KYCQuestionnaireElement) => {
    addView({
      sectionId: run[0].id,
      title: page.title,
      pageHeader: header,
      elements: run,
    });
  };

  for (const group of groupSiblings(page.elements)) {
    if (group.run) {
      addRun(group.run);
      continue;
    }

    // A level-0 header block: its BLOCK children are the sections.
    const header = group.block;
    pageHeader ??= header;

    for (const child of groupSiblings(header.elements)) {
      if (child.run) {
        addRun(child.run, header);
      } else if (shouldShow(child.block)) {
        addView({
          sectionId: child.block.id,
          title: child.block.title || page.title,
          pageHeader: header,
          elements: [child.block],
        });
      }
    }
  }

  // A page without any view still renders its header.
  if (views.length === 0) {
    addView({
      sectionId: page.id,
      title: page.title,
      pageHeader,
      elements: [],
    });
  }

  return views;
};

/**
 * Splits the pages (trigger-filtered leaf NAVs) into the ordered list of
 * mobile views: one per visible level-1 BLOCK, one per run of consecutive
 * non-BLOCK elements, and one for a page that yields nothing else.
 */
export const getSectionViews = (
  pages: KYCQuestionnaireElement[],
  shouldShow: (element: KYCQuestionnaireElement) => boolean,
): SectionView[] => pages.flatMap((page) => getPageViews(page, shouldShow));

const getViewQuestions = (view: SectionView): KYCQuestionDefinition[] =>
  view.elements.flatMap((element) => getElementQuestions(element));

/** The `sectionId` of the view whose subtree holds the question. */
export const getQuestionSectionID = (
  views: SectionView[],
  questionID: string,
): string | undefined =>
  views.find((view) =>
    getViewQuestions(view).some(({ id }) => id === questionID),
  )?.sectionId;

type ResolveSectionIdArgs = {
  /** The views the user can see right now (`sectionViews`). */
  views: SectionView[];
  /** Every view in document order, hidden ones included (`allSectionViews`). */
  allViews: SectionView[];
  pageId: string;
  sectionId?: string;
};

/**
 * The section to show for `pageId` when one section renders at a time:
 * `sectionId` when it is a visible view of the page; the nearest following
 * visible view of the page (or the previous one) when a trigger hides it;
 * otherwise the first view of the page. `undefined` when the page has no
 * visible view.
 */
export const resolveSectionId = ({
  views,
  allViews,
  pageId,
  sectionId,
}: ResolveSectionIdArgs): string | undefined => {
  const pageViews = views.filter((view) => view.pageId === pageId);
  const isVisible = (id: string) =>
    pageViews.some((view) => view.sectionId === id);

  if (sectionId && isVisible(sectionId)) {
    return sectionId;
  }

  const pageAllViews = allViews.filter((view) => view.pageId === pageId);
  const hiddenIndex = pageAllViews.findIndex(
    (view) => view.sectionId === sectionId,
  );
  if (hiddenIndex !== -1) {
    const following = pageAllViews
      .slice(hiddenIndex + 1)
      .find((view) => isVisible(view.sectionId));
    const previous = pageAllViews
      .slice(0, hiddenIndex)
      .reverse()
      .find((view) => isVisible(view.sectionId));
    const nearest = following ?? previous;
    if (nearest) {
      return nearest.sectionId;
    }
  }

  return pageViews[0]?.sectionId;
};

/**
 * Status of a view for the navigation drawer: `error` when one of its
 * questions has an error, `completed` when every shown required question has
 * an answer (a GROUP counts once a row has a value), `default` otherwise.
 */
export const getSectionStatus = (
  view: SectionView,
  answers: Record<string, FormAnswer | undefined>,
  errorQuestionIDs: string[],
  hiddenQuestionPaths: string[],
): SectionStatus => {
  const questions = getViewQuestions(view);

  if (questions.some(({ id }) => errorQuestionIDs.includes(id))) {
    return 'error';
  }

  const isCompleted = questions
    .filter(
      ({ id, optional, hidden }) =>
        !optional && !hidden && !hiddenQuestionPaths.includes(id),
    )
    .every(({ id }) => questionHasAnswer(id, answers));

  return isCompleted ? 'completed' : 'default';
};
